<template>

  <div class="container" style="background-color: #FAFAFA;" v-if="mysmartcard">


    <!-- Conditional rendering based on vipDialna -->
    <!-- <div v-if="mysmartcard.vipDialna === 'Enterprise'">
    <EntrepriseInfo :mysmartcard="mysmartcard" />
  </div>
<div v-else>-->
    <ProfileCard :mysmartcard="mysmartcard" :is-vip="isVip" />
    <VideoPage :mysmartcard="mysmartcard" />
    <LinksPage :mysmartcard="mysmartcard" />
    <p class="text-center text-xs text-white">Version 1.0.0</p>
  </div>
  <!-- </div>-->
  <div v-else>
    <LoadingPage />
  </div>

</template>

<script setup lang="ts">

// @ts-ignore - Vue SFC default export
import ProfileCard from "@/components/ProfileCard.vue";
// @ts-ignore - Vue SFC default export
import LoadingPage from "@/components/LoadingPage.vue";
// @ts-ignore - Vue SFC default export
import VideoPage from '@/components/VideosPage.vue';
// @ts-ignore - Vue SFC default export
import LinksPage from "@/components/LinksPage.vue";
import { ref, computed, watchEffect, watch } from "vue";
import { trackEvent } from "@/utils/analytics";
import { auth, contactDocument } from "@/lib/firebase";
import { signInAnonymously, UserCredential } from "firebase/auth";
import { where, query, getDocs, Query, DocumentData } from "firebase/firestore";
import type { QwikUCard } from '@/types';

type Props = {
  shareid?: string | null;
  // ... other props
};

const props = withDefaults(defineProps<Props>(), {
  shareid: null
});

// State
const userCredential = ref<UserCredential | null>(null);
const sharedIdDoc = ref<string | null>(null);
const mysmartcard = ref<QwikUCard | null>(null);

// Calculate isVip based on business logic
const isVip = computed(() => {
  const card = mysmartcard.value;
  if (!card) return false;

  // Check if vipDialna is not null and not empty
  if (card.vipDialna != null && card.vipDialna.trim() !== '') {
    return true;
  }

  // Check if IsVIP is true
  if (card.IsVIP) {
    const expirationDate = card._AppPurchase?.Expiration_Date;

    // If Expiration_Date is empty, isVip is true
    if (!expirationDate || expirationDate.trim() === '') {
      return true;
    }

    // If Expiration_Date is not empty, check if it's greater than today
    // Format: 2026-11-02T06:36:54Z (ISO 8601)
    try {
      const expiration = new Date(expirationDate);
      const now = new Date();

      // Check if expiration date is in the future
      return expiration > now;
    } catch (error) {
      console.error('Error parsing expiration date:', error);
      return false;
    }
  }

  return false;
});

// Separate authentication logic
const handleAuthentication = async (): Promise<UserCredential | null> => {
  try {
    if (!userCredential.value) {
      userCredential.value = await signInAnonymously(auth);
    }
    return userCredential.value;
  } catch (error) {
    console.error("Authentication failed:", error);
    return null;
  }
};

// Separate query creation logic
const createContactQuery = (shareId: string): Query<QwikUCard> | null => {
  if (!shareId) return null;
  return query(contactDocument, where("_AppShare.cardIdShare", "==", shareId));
};

// Watch for changes and fetch data
watchEffect(() => {
  (async () => {
    try {
      const credential = await handleAuthentication();

      if (!credential) {
        console.error("No valid credentials");
        return;
      }

      // Only update if shareid changed
      if (sharedIdDoc.value !== props.shareid) {
        sharedIdDoc.value = props.shareid;
        if (sharedIdDoc.value) {
          const q = createContactQuery(sharedIdDoc.value);
          if (q) {
            const querySnapshot = await getDocs(q);
            const firstDoc = querySnapshot.docs[0];
            if (firstDoc) {
              const data = firstDoc.data() as QwikUCard;
              data.id = firstDoc.id;
              mysmartcard.value = data;
            }
          }
        }
      }
    } catch (error) {
      console.error("Error in watch effect:", error);
    }
  })();
}, {
  flush: 'post'
});

// Analytics
watch(mysmartcard, (newCard) => {
  if (newCard?.id && isVip.value) {
    trackEvent(newCard.id, 'visit', {}, newCard.companyId);
  }
});






</script>

<style src="@/assets/styles/shared-styles.css"></style>

<style scoped>
.container {
  width: 100%;
  max-width: 100vw;
  min-width: 350px;
  overflow-x: auto;
  padding: 0;
  box-sizing: border-box;
}

/* Large screens - set max-width to prevent content from being too wide */
@media screen and (min-width: 1200px) {
  .container {
    max-width: 1200px;
    margin: 0 auto;
  }
}

/* Medium screens */
@media screen and (min-width: 768px) and (max-width: 1199px) {
  .container {
    max-width: 100%;
    padding: 0 1rem;
  }
}

/* Small screens - hide horizontal scroll for screens >= 350px */
@media screen and (min-width: 350px) and (max-width: 767px) {
  .container {
    width: 100%;
    max-width: 100vw;
    min-width: 350px;
    padding: 0;
    overflow-x: hidden;
  }
}

/* Very small screens - allow horizontal scroll */
@media screen and (max-width: 349px) {
  .container {
    width: 350px;
    min-width: 350px;
    overflow-x: auto;
    overflow-y: hidden;
  }
}

/* Ensure all child components respect container width */
.container>* {
  max-width: 100%;
  box-sizing: border-box;
}
</style>